import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import EmailIcon from '@mui/icons-material/Email';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import MoreMenu from 'src/components/MoreMenu/MoreMenu';
import dateFormatType from 'src/utils/dateFormatType';
import selectFormatDate from 'src/utils/selectDateFormat';
import { formatPositiveAmount } from 'src/utils/formatAmout';
import { HEADING_TEXT_COLOR, HAIRLINE_BORDER_COLOR, LINK_COLOR } from './styleConstants';

export const PaymentComplementActions = {
  VIEW_PDF: 'VIEW_PDF',
  SEND_TO_CLIENT: 'SEND_TO_CLIENT',
  ASSOCIATE_CONTRACT: 'ASSOCIATE_CONTRACT'
};

const getComplementMenuOptions = (hasDocument, hasContract) => {
  const options = [
    {
      name: PaymentComplementActions.VIEW_PDF,
      label: 'TRANSACTION.DETAIL.VIEW_PDF',
      disabled: !hasDocument,
      icon: <FileOpenIcon />
    },
    {
      name: PaymentComplementActions.SEND_TO_CLIENT,
      label: 'TRANSACTION.DETAIL.SEND_TO_CLIENT',
      icon: <EmailIcon />
    }
  ];

  if (!hasContract) {
    options.push({
      name: PaymentComplementActions.ASSOCIATE_CONTRACT,
      label: 'TRANSACTION.ASSOCIATE_TRANSACTION_TO_CONTRACT',
      icon: <PublishedWithChangesIcon />
    });
  }

  return options;
};

const PaymentComplementRow = ({
  id,
  invoiceDbId,
  folio,
  date,
  origin,
  contractId,
  contractFolioWithoutPrefix,
  amountIvaIncluded,
  document,
  onViewPdf,
  onSendToClient,
  onAssociateContract
}) => {
  const { t } = useTranslation();

  const handleClickOption = option => {
    if (option === PaymentComplementActions.VIEW_PDF) {
      onViewPdf(id);
    }
    if (option === PaymentComplementActions.SEND_TO_CLIENT) {
      onSendToClient(id);
    }
    if (option === PaymentComplementActions.ASSOCIATE_CONTRACT) {
      onAssociateContract(id);
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '92px minmax(0, 1fr) auto 34px',
        gap: '8px 14px',
        alignItems: 'center',
        minHeight: '86px',
        padding: '14px 18px',
        borderBottom: `1px solid ${HAIRLINE_BORDER_COLOR}`
      }}
    >
      <Typography sx={{ color: '#7b838e', fontSize: '13px' }}>
        {selectFormatDate(date, dateFormatType.DAY)}
      </Typography>
      <Box sx={{ minWidth: 0, display: 'grid', gap: '3px' }}>
        <Typography
          component={Link}
          to={`/invoice/${invoiceDbId}`}
          sx={{ fontWeight: 800, color: LINK_COLOR, textDecoration: 'none' }}
        >
          {folio}
        </Typography>
        <Typography sx={{ color: '#7a838d', fontSize: '12px' }}>
          {[
            origin ? t(`TRANSACTION.ORIGIN.${origin}`) : null,
            contractFolioWithoutPrefix || t('TRANSACTION.NO_CONTRACT_ASSOCIATED')
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          color: HEADING_TEXT_COLOR,
          textAlign: 'right',
          whiteSpace: 'nowrap'
        }}
      >
        {formatPositiveAmount(amountIvaIncluded)}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <MoreMenu
          deleteDisabled
          editDisabled
          options={getComplementMenuOptions(
            Boolean(document?.documentUrl),
            Boolean(contractId)
          )}
          handleClickOption={handleClickOption}
        />
      </Box>
    </Box>
  );
};

PaymentComplementRow.propTypes = {
  id: PropTypes.number.isRequired,
  invoiceDbId: PropTypes.number,
  folio: PropTypes.string,
  date: PropTypes.string.isRequired,
  origin: PropTypes.string,
  contractId: PropTypes.number,
  contractFolioWithoutPrefix: PropTypes.string,
  amountIvaIncluded: PropTypes.number.isRequired,
  document: PropTypes.shape({ documentUrl: PropTypes.string }),
  onViewPdf: PropTypes.func.isRequired,
  onSendToClient: PropTypes.func.isRequired,
  onAssociateContract: PropTypes.func.isRequired
};

PaymentComplementRow.defaultProps = {
  invoiceDbId: null,
  folio: null,
  origin: null,
  contractId: null,
  contractFolioWithoutPrefix: null,
  document: null
};

export default PaymentComplementRow;
